import React, { createContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithCredential,
  signOut,
  sendPasswordResetEmail,
  updateProfile as updateFirebaseProfile,
  GoogleAuthProvider
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { syncUserProfileWithPosts, syncUserProfileWithComments } from '../utils/profileSync';
import googleAuthHelper from '../utils/firebaseAuthHelper';

type AuthContextType = {
  currentUser: User | null;
  loading: boolean;
  registerWithEmail: (email: string, password: string, displayName: string) => Promise<User>;
  loginWithEmail: (email: string, password: string) => Promise<User>;
  loginWithGoogle: () => Promise<User>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  userData: UserData | null;
  updateUserProfile: (displayName: string, photoURL: string) => Promise<void>;
};

export interface UserData {
  uid: string;
  displayName: string;
  email: string;
  photoURL: string;
  coverPhotoURL?: string;
  bio?: string;
  location?: string;
  followers?: number;
  following?: number;
  createdAt: any;
  updatedAt: any;
}

export const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  registerWithEmail: async () => { throw new Error('Not implemented'); },
  loginWithEmail: async () => { throw new Error('Not implemented'); },
  loginWithGoogle: async () => { throw new Error('Not implemented'); },
  logout: async () => { throw new Error('Not implemented'); },
  resetPassword: async () => { throw new Error('Not implemented'); },
  userData: null,
  updateUserProfile: async () => { throw new Error('Not implemented'); },
});

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Timeout de seguridad: si Firebase no responde en 3 segundos, mostrar la app
    const safetyTimeout = setTimeout(() => {
      setLoading(false);
    }, 3000);

    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setLoading(false); // Mostrar la UI inmediatamente

      // Cargar datos del usuario en segundo plano (no bloquea la UI)
      if (user) {
        (async () => {
          try {
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);

            if (userDoc.exists()) {
              setUserData(userDoc.data() as UserData);
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
          }
        })();
      } else {
        setUserData(null);
      }
    });

    // Configurar callbacks para Android WebView
    // Estas funciones son llamadas por MainActivity.java cuando se completa la autenticación
    (window as any).androidAuthSuccess = async (userInfo: { name: string; email: string; uid: string; idToken?: string }) => {
      console.log('Android auth success callback recibido:', userInfo);

      try {
        if (userInfo.idToken) {
          // Usar el idToken de Google para autenticar en el WebView
          console.log('Autenticando WebView con idToken de Google...');
          const credential = GoogleAuthProvider.credential(userInfo.idToken);
          const result = await signInWithCredential(auth, credential);
          console.log('WebView autenticado exitosamente:', result.user.displayName);
          // El listener onAuthStateChanged se encargará de actualizar el estado
        } else {
          // Si no hay idToken, recargar como fallback
          console.log('No hay idToken, recargando página...');
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      } catch (error) {
        console.error('Error autenticando WebView con idToken:', error);
        // Si falla, intentar recargar como fallback
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    };

    (window as any).androidRefreshSession = () => {
      console.log('Android refresh session callback recibido');
      // Forzar recarga de la página para sincronizar la sesión
      window.location.reload();
    };

    (window as any).androidAuthError = (error: string) => {
      console.error('Android auth error:', error);
      setLoading(false);
    };

    return () => {
      clearTimeout(safetyTimeout);
      unsubscribe();
      // Limpiar callbacks de Android
      delete (window as any).androidAuthSuccess;
      delete (window as any).androidRefreshSession;
      delete (window as any).androidAuthError;
    };
  }, []);

  // Validar formato de email
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validar fortaleza de contraseña
  const validatePassword = (password: string): boolean => {
    // Mínimo 8 caracteres, al menos una letra y un número
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;
    return passwordRegex.test(password);
  };

  const registerWithEmail = async (email: string, password: string, displayName: string): Promise<User> => {
    // Validar datos antes de enviar a Firebase
    if (!email || !password || !displayName) {
      throw new Error('Todos los campos son obligatorios');
    }

    if (!validateEmail(email)) {
      throw new Error('El formato del email no es válido');
    }

    if (!validatePassword(password)) {
      throw new Error('La contraseña debe tener al menos 8 caracteres, una letra y un número');
    }

    if (displayName.length < 3) {
      throw new Error('El nombre debe tener al menos 3 caracteres');
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Update profile with display name
      if (auth.currentUser) {
        await updateFirebaseProfile(auth.currentUser, {
          displayName,
        });
      }

      // Create user document in Firestore
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      await setDoc(userDocRef, {
        uid: userCredential.user.uid,
        displayName,
        email,
        photoURL: userCredential.user.photoURL || '',
        followers: 0,
        following: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return userCredential.user;
    } catch (error) {
      throw error;
    }
  };

  const loginWithEmail = async (email: string, password: string): Promise<User> => {
    // Validar datos antes de enviar a Firebase
    if (!email || !password) {
      throw new Error('Email y contraseña son obligatorios');
    }

    if (!validateEmail(email)) {
      throw new Error('El formato del email no es válido');
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error: any) {
      // Traducir errores de Firebase a mensajes amigables
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        throw new Error('Email o contraseña incorrectos');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Demasiados intentos fallidos. Intenta más tarde o restablece tu contraseña');
      } else {
        throw new Error('Error de inicio de sesión: ' + (error.message || 'Intenta de nuevo más tarde'));
      }
    }
  };

  const loginWithGoogle = async (): Promise<User> => {
    try {
      console.log('Iniciando autenticación con Google desde AuthContext...');

      // Usar nuestro helper para manejar la autenticación con Google
      const userCredential = await googleAuthHelper.signInWithGoogle(auth, db);
      console.log('Usuario autenticado con Google:', userCredential.user.displayName);

      return userCredential.user;
    } catch (error: any) {
      console.error('Error en inicio de sesión con Google:', error);

      // El helper ya maneja los errores específicos, solo re-lanzamos el error
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    if (!email) {
      throw new Error('El email es obligatorio');
    }

    if (!validateEmail(email)) {
      throw new Error('El formato del email no es válido');
    }

    try {
      // Configurar la URL de acción para el restablecimiento de contraseña
      // Esto asegura que el enlace de restablecimiento redirija a nuestra página personalizada
      const actionCodeSettings = {
        url: `${window.location.origin}/login`, // Redirecciona al login después de restablecer
        handleCodeInApp: false // Manejar el código en la app web, no en una app móvil
      };

      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      console.log('Email de restablecimiento enviado a:', email);
    } catch (error: any) {
      console.error('Error al enviar email de restablecimiento:', error);

      if (error.code === 'auth/user-not-found') {
        // No revelar si el email existe o no (seguridad)
        // Simular éxito para evitar enumeración de usuarios
        return;
      }

      // Traducir errores comunes a mensajes más amigables
      let errorMessage = 'Error al enviar el email de restablecimiento. Intenta de nuevo más tarde.';

      switch (error.code) {
        case 'auth/invalid-email':
          errorMessage = 'El formato del email no es válido.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Has realizado demasiadas solicitudes. Intenta de nuevo más tarde.';
          break;
        default:
          errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  };

  const updateUserProfile = async (displayName: string, photoURL: string): Promise<void> => {
    if (!currentUser) {
      throw new Error('No hay usuario autenticado');
    }

    if (!displayName && !photoURL) {
      throw new Error('Debe proporcionar al menos un campo para actualizar');
    }

    try {
      const updates: { [key: string]: any } = {
        updatedAt: new Date()
      };

      if (displayName) {
        updates.displayName = displayName;
      }

      if (photoURL) {
        updates.photoURL = photoURL;
      }

      // Update Firebase Auth profile if available
      if (auth.currentUser) {
        await updateFirebaseProfile(auth.currentUser, {
          displayName: displayName || undefined,
          photoURL: photoURL || undefined
        });
      }

      // Update user document in Firestore
      const userDocRef = doc(db, 'users', currentUser.uid);
      await setDoc(userDocRef, updates, { merge: true });

      // Sync profile changes with posts and comments
      const syncUpdates: { [key: string]: string } = {};

      if (displayName) {
        syncUpdates.userName = displayName;
      }

      if (photoURL) {
        syncUpdates.userPhotoURL = photoURL;
      }

      await Promise.all([
        syncUserProfileWithPosts(currentUser.uid, syncUpdates),
        syncUserProfileWithComments(currentUser.uid, syncUpdates)
      ]);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    loading,
    registerWithEmail,
    loginWithEmail,
    loginWithGoogle,
    logout,
    resetPassword,
    userData,
    updateUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
